import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type RouterService from '@ember/routing/router-service';
import { PhWarningCircle, PhArrowClockwise, PhHouse } from 'ember-phosphor-icons';
import Button from 'tio-reimagined/components/button';

interface RouteErrorSignature {
  Args: {
    /** The error object from the failed route model hook */
    error: unknown;
    /** Title text (default: "Something went wrong") */
    title?: string;
    /** Description text (default: "We couldn't load this page. Please try again.") */
    description?: string;
    /** Retry button label (default: "Try Again") */
    retryLabel?: string;
    /** Home link label (default: "Go Home") */
    homeLabel?: string;
    /** Route to use for "Go Home" (default: "authenticated") */
    homeRoute?: string;
  };
  Element: HTMLDivElement;
}

export default class TioRouteErrorComponent extends Component<RouteErrorSignature> {
  @service declare router: RouterService;

  get title() {
    return this.args.title ?? 'Something went wrong';
  }

  get description() {
    return this.args.description ?? "We couldn't load this page. Please try again.";
  }

  get retryLabel() {
    return this.args.retryLabel ?? 'Try Again';
  }

  get homeLabel() {
    return this.args.homeLabel ?? 'Go Home';
  }

  get homeRoute() {
    return this.args.homeRoute ?? 'authenticated';
  }

  get errorDetail(): string | null {
    const error = this.args.error;
    if (!error) return null;

    // Handle Ember Data / JSON API errors
    if (typeof error === 'object' && 'errors' in (error as Record<string, unknown>)) {
      const rawErrors = (error as { errors: unknown }).errors;
      if (Array.isArray(rawErrors) && rawErrors.length > 0) {
        const first = rawErrors[0] as { status?: string; detail?: string } | null | undefined;
        if (first?.status && first?.detail) {
          return `${first.status}: ${first.detail}`;
        }
        if (first?.detail) return first.detail;
      }
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return error.message;
    }

    return null;
  }

  retry = () => {
    window.location.reload();
  };

  goHome = () => {
    this.router.transitionTo(this.homeRoute);
  };

  <template>
    <div data-test-route-error class="tio-place-center text-center py-24 px-6" ...attributes>
      <div class="bg-red-50 rounded-full p-4 mb-4">
        <PhWarningCircle @size={{32}} @weight="fill" class="text-red-500" />
      </div>
      <h3 data-test-route-error-title class="tio-text-heading tio-fg-primary mb-1">
        {{this.title}}
      </h3>
      <p data-test-route-error-description class="tio-fg-muted max-w-sm mb-1">
        {{this.description}}
      </p>
      {{#if this.errorDetail}}
        <p data-test-route-error-detail class="text-sm tio-fg-disabled max-w-md mb-6 font-mono">
          {{this.errorDetail}}
        </p>
      {{else}}
        <div class="mb-6"></div>
      {{/if}}
      <div class="tio-flex">
        <Button
          data-test-route-error-retry
          @intent="primary"
          @icon={{PhArrowClockwise}}
          {{on "click" this.retry}}
        >
          {{this.retryLabel}}
        </Button>
        <Button
          data-test-route-error-home
          @appearance="outlined"
          @icon={{PhHouse}}
          {{on "click" this.goHome}}
        >
          {{this.homeLabel}}
        </Button>
      </div>
    </div>
  </template>
}

export type { RouteErrorSignature };
